import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'

/** A guest has no account, so they name the address and the gym they sign at. */
export interface GuestEmail {
  gymSlug: string
  email: string
}

export interface EmailVerified {
  email: string
  verifiedAt: string
  /** A guest hands this back with their signature. */
  verificationId: string
}

export type EmailCodeError =
  | 'CODE_INVALID'
  | 'CODE_EXPIRED'
  | 'TOO_MANY_ATTEMPTS'
  | 'TOO_SOON'
  | 'TOO_MANY'
  | 'EMAIL'
  | 'NETWORK'
  | 'UNKNOWN'

const KNOWN: readonly string[] = [
  'CODE_INVALID',
  'CODE_EXPIRED',
  'TOO_MANY_ATTEMPTS',
  'TOO_SOON',
  'TOO_MANY',
  'EMAIL',
]

/** How long the "send again" button waits, matching the Worker's own limit. */
const RESEND_SECONDS = 60

/**
 * Sends and checks the six-digit code that proves an email address.
 *
 * With `guest`, the address and gym go in the request and no token is sent. For
 * someone signed in, the Worker reads the address off their account, so this
 * never says which one to verify.
 */
export function useEmailCode(guest?: GuestEmail) {
  const { getAccessTokenSilently } = useAuth()
  const { i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<EmailCodeError | null>(null)
  const [cooldown, setCooldown] = useState(0)
  const [alreadyVerified, setAlreadyVerified] = useState<EmailVerified | null>(null)
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)

  const startCooldown = useCallback((seconds: number) => {
    if (timer.current) clearInterval(timer.current)
    setCooldown(seconds)
    timer.current = setInterval(() => {
      setCooldown((left) => {
        if (left <= 1 && timer.current) {
          clearInterval(timer.current)
          timer.current = null
        }
        return Math.max(0, left - 1)
      })
    }, 1000)
  }, [])

  useEffect(
    () => () => {
      if (timer.current) clearInterval(timer.current)
    },
    [],
  )

  const post = useCallback(
    async (path: string, body: Record<string, unknown>) => {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (!guest) headers.Authorization = `Bearer ${await getAccessTokenSilently()}`
      return fetch(`${apiUrl}${path}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...body,
          ...(guest ? { gym_slug: guest.gymSlug, email: guest.email } : {}),
        }),
      })
    },
    // getAccessTokenSilently is stable per Auth0 SDK contract
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiUrl, guest?.gymSlug, guest?.email],
  )

  const send = useCallback(async () => {
    setSending(true)
    setError(null)
    try {
      const res = await post('/email-code', { language: i18n.language.slice(0, 2) })
      const data = (await res.json().catch(() => ({}))) as {
        code?: string
        retry_after?: number
        verified?: boolean
        email?: string
        verified_at?: string
      }
      if (res.ok && data.verified && data.email && data.verified_at) {
        // Verified on another device since this screen loaded.
        setAlreadyVerified({ email: data.email, verifiedAt: data.verified_at, verificationId: '' })
        return
      }
      if (res.ok) {
        setSent(true)
        startCooldown(RESEND_SECONDS)
        return
      }
      if (data.code === 'TOO_SOON') {
        // A code is already on its way: let them type it, and wait to resend.
        setSent(true)
        startCooldown(data.retry_after ?? RESEND_SECONDS)
        return
      }
      setError(data.code && KNOWN.includes(data.code) ? (data.code as EmailCodeError) : 'UNKNOWN')
    } catch {
      setError('NETWORK')
    } finally {
      setSending(false)
    }
  }, [post, i18n.language, startCooldown])

  const verify = useCallback(
    async (code: string): Promise<EmailVerified | null> => {
      setChecking(true)
      setError(null)
      try {
        const res = await post('/email-code/verify', { code })
        const data = (await res.json().catch(() => ({}))) as {
          code?: string
          email?: string
          verified_at?: string
          verification_id?: string
        }
        if (res.ok && data.email && data.verified_at && data.verification_id)
          return {
            email: data.email,
            verifiedAt: data.verified_at,
            verificationId: data.verification_id,
          }
        setError(data.code && KNOWN.includes(data.code) ? (data.code as EmailCodeError) : 'UNKNOWN')
        return null
      } catch {
        setError('NETWORK')
        return null
      } finally {
        setChecking(false)
      }
    },
    [post],
  )

  return { send, verify, sending, sent, checking, error, cooldown, alreadyVerified }
}
